// ──────────────────────────────────────────────
// STRANGELOVE OS — Comprehensive Mock Data
// Mirrors the exact shape of PRD Section 4.3 API responses.
// Phase 4 swaps these for real API calls.
// ──────────────────────────────────────────────

// ── Types ──

export interface Commander {
  id: string;
  email: string;
  name: string;
  loginStreak: number;
  xpTotal: number;
  lastLoginAt: string;
}

export interface Opportunity {
  id: string;
  title: string;
  description: string;
  sourceType: "pain_scanner" | "builder_intel" | "trend_signal" | "manual";
  sourceUrl: string;
  sourceCommunity: string;
  industry: string;
  status: "discovered" | "deep_dive" | "building" | "launched" | "archived" | "rejected";
  compositeScore: number;
  painIntensity: number;
  purchasingPower: number;
  targetability: number;
  marketGrowth: number;
  whitespace: number;
  monopolyPotential: number;
  domainProximity: number;
  distributionAdvantage: number;
  buildFeasibility: number;
  speedToRevenue: number;
  rawSignals: RawSignal[];
  competitiveLandscape: Competitor[];
  aiAnalysis: string;
  ventureId: string | null;
  discoveredAt: string;
  scoredAt: string;
  // V2 fields
  opportunityName: string | null;
  elevatorPitch: string | null;
  subIndustry: string | null;
  painSummary: string | null;
  proposedSolution: string | null;
  saturationLevel: "blue_ocean" | "cracked_door" | "red_ocean" | null;
  saturationDetail: string | null;
  existingCompetitors: ExistingCompetitor[] | null;
  blueOceanAngle: string | null;
  signalCount: number | null;
  sourcePlatforms: string[] | null;
  confidence: "high" | "medium" | "low" | null;
  clusterIds: string[] | null;
  legacy: boolean;
}

export interface ExistingCompetitor {
  name: string;
  weakness: string;
  pricing?: string;
}

export interface RawSignalRecord {
  id: string;
  scanLogId: string | null;
  sourcePlatform: string;
  sourceType: string;
  title: string;
  body: string;
  url: string | null;
  community: string | null;
  engagementScore: number;
  secondaryEngagement: number | null;
  classification: string | null;
  industry: string | null;
  painDescription: string | null;
  targetAudience: string | null;
  matchedKeywords: string[] | null;
  keywordWeight: number | null;
  clusterId: string | null;
  status: string;
  createdAt: string;
}

export interface PainCluster {
  id: string;
  theme: string;
  description: string | null;
  industry: string | null;
  signalCount: number;
  sourcePlatforms: string[] | null;
  sourceCount: number;
  representativeQuotes: string[] | null;
  status: string;
  opportunityId: string | null;
  createdAt: string;
}

export interface PainAnalytics {
  totalSignals: number;
  totalClusters: number;
  qualifiedClusters: number;
  synthesizedOpps: number;
  industryBreakdown: { industry: string; count: number }[];
}

export interface DeepDiveSection {
  id: string;
  opportunityId: string;
  section: string;
  content: string;
  generatedAt: string;
}

export interface RawSignal {
  source: string;
  quote: string;
  engagement: number;
  date: string;
}

export interface Competitor {
  name: string;
  description: string;
  gap: string;
}

export interface DeepDiveMessage {
  id: string;
  opportunityId: string;
  role: "commander" | "strangelove";
  content: string;
  createdAt: string;
}

export interface RevenueStream {
  id: string;
  name: string;
  targetMonthly: number;
  targetClients: number;
  avgDealSizeLow: number;
  avgDealSizeHigh: number;
  isActive: boolean;
  leverageLevel: "high" | "medium" | "low";
  sortOrder: number;
}

export interface RevenueEntry {
  id: string;
  streamId: string;
  streamName: string;
  amount: number;
  clientName: string;
  description: string;
  date: string;
  isRecurring: boolean;
}

export interface Venture {
  id: string;
  opportunityId: string | null;
  name: string;
  description: string;
  status: "planning" | "building" | "launched" | "growing" | "sunset";
  githubUrl: string;
  liveUrl: string;
  monthlyRevenue: number;
  launchDate: string | null;
}

export interface Milestone {
  id: string;
  title: string;
  description: string;
  metricType: "revenue" | "clients" | "ventures_launched" | "opportunities_evaluated" | "custom";
  targetValue: number;
  currentValue: number;
  targetDate: string;
  status: "not_started" | "in_progress" | "at_risk" | "achieved";
}

export interface AdvantageProfile {
  industriesKnown: { name: string; proximityRating: number }[];
  skills: string[];
  distributionChannels: { name: string; strength: number }[];
  productsShipped: { name: string; category: string; date: string }[];
  budgetMonthlyMax: number;
}

export interface ScanSourceDetail {
  sourceName: string;
  platform: string;
  status: "success" | "timeout" | "error" | "skipped" | "no_signals";
  fetchAttempted: boolean;
  fetchDurationMs: number;
  httpStatus?: number;
  rawItemsFetched: number;
  itemsAfterDedup: number;
  itemsAfterKeywordFilter: number;
  keywordFilterDropped: number;
  sampleDroppedTitles?: string[];
  signalsStored: number;
  errorMessage?: string;
  errorType?: string;
}

export interface ScanLog {
  id: string;
  agentType: "pain_scanner" | "builder_intel" | "trend_analyst";
  sourcesScanned: string[];
  opportunitiesFound: number;
  opportunitiesScored: number;
  startedAt: string;
  completedAt: string | null;
  status: "running" | "completed" | "failed";
  errorLog?: string | null;
  details?: ScanSourceDetail[] | null;
}

export interface Achievement {
  id: string;
  badgeKey: string;
  title: string;
  description: string;
  icon: string;
  xpReward: number;
  earnedAt: string | null;
}

export interface Briefing {
  greeting: string;
  revenue: {
    currentMonth: number;
    target: number;
    percentage: number;
    gap: number;
  };
  overnightScan: {
    newOpportunities: number;
    topScore: number;
    topOpportunityTitle: string;
  };
  topActions: string[];
  strangeloveQuote: string;
}

// ── Mock Data ──

export const mockCommander: Commander = {
  id: "cmd-001",
  email: "commander@digitalempire.com",
  name: "The Commander",
  loginStreak: 12,
  xpTotal: 450,
  lastLoginAt: new Date().toISOString(),
};

export const mockOpportunities: Opportunity[] = [
  {
    id: "opp-001",
    title: "Veterinary insurance claim automation",
    description: "Multiple threads across r/veterinary and r/vettech complaining about manual insurance claim submission. Clinics spending 2-3 hours daily on paperwork. No dominant SaaS solution exists.",
    sourceType: "pain_scanner",
    sourceUrl: "https://reddit.com/r/veterinary/comments/abc123",
    sourceCommunity: "r/veterinary",
    industry: "Veterinary / Pet Care",
    status: "deep_dive",
    compositeScore: 92,
    painIntensity: 9,
    purchasingPower: 8,
    targetability: 9,
    marketGrowth: 7,
    whitespace: 9,
    monopolyPotential: 7,
    domainProximity: 5,
    distributionAdvantage: 8,
    buildFeasibility: 8,
    speedToRevenue: 8,
    rawSignals: [
      { source: "r/veterinary", quote: "Our insurance claims are a complete nightmare. We spend 3 hours every day just doing paperwork instead of treating animals.", engagement: 247, date: "2026-02-25" },
      { source: "r/vettech", quote: "I literally had to call the insurance company 4 times to get one claim processed. There has to be a better way.", engagement: 189, date: "2026-02-26" },
      { source: "r/veterinary", quote: "Just lost $12,000 in unpaid claims because we missed the filing deadline. We need automation desperately.", engagement: 312, date: "2026-02-27" },
    ],
    competitiveLandscape: [
      { name: "PetDesk", description: "Appointment scheduling and client communication for vet clinics", gap: "No insurance claim features at all" },
      { name: "Shepherd Veterinary Software", description: "Practice management software", gap: "Insurance module is basic — manual entry, no auto-submission" },
      { name: "Trupanion Express", description: "Direct pay at point of care", gap: "Only works with Trupanion policies, not multi-carrier" },
    ],
    aiAnalysis: "Commander, this opportunity exhibits extraordinary market characteristics. The pain is visceral — veterinary professionals are drowning in paperwork while animals wait. The purchasing power is confirmed (clinics have operational budgets), and the whitespace is remarkable. No one has built a proper multi-carrier claim automation tool. Your distribution advantage through content marketing to this niche is strong. I rate this a STRIKE NOW.",
    ventureId: null,
    discoveredAt: "2026-02-25T08:00:00Z",
    scoredAt: "2026-02-25T08:05:00Z",
    opportunityName: "VetClaim AI",
    elevatorPitch: "Automated multi-carrier insurance claim submission for veterinary clinics, eliminating 3 hours of daily paperwork.",
    subIndustry: "Veterinary Software",
    painSummary: "Veterinary clinics spend 2-3 hours daily on manual insurance claim paperwork, losing $12K+ from missed filing deadlines.",
    proposedSolution: "AI-powered claim automation tool that auto-submits to all major pet insurance carriers with status tracking.",
    saturationLevel: "blue_ocean",
    saturationDetail: "No multi-carrier claim automation exists for veterinary clinics.",
    existingCompetitors: [
      { name: "PetDesk", weakness: "No insurance claim features" },
      { name: "Shepherd Veterinary Software", weakness: "Manual entry only" },
    ],
    blueOceanAngle: "First multi-carrier automated claims platform built specifically for vet clinics.",
    signalCount: 3,
    sourcePlatforms: ["reddit"],
    confidence: "high",
    clusterIds: [],
    legacy: false,
  },
  {
    id: "opp-002",
    title: "HVAC contractor scheduling chaos",
    description: "r/HVAC threads exploding with complaints about double-bookings, no-shows, and scheduling via text message. Small contractors with 3-10 techs have no affordable solution.",
    sourceType: "pain_scanner",
    sourceUrl: "https://reddit.com/r/HVAC/comments/def456",
    sourceCommunity: "r/HVAC",
    industry: "Home Services / HVAC",
    status: "discovered",
    compositeScore: 84,
    painIntensity: 8,
    purchasingPower: 7,
    targetability: 8,
    marketGrowth: 7,
    whitespace: 7,
    monopolyPotential: 5,
    domainProximity: 4,
    distributionAdvantage: 7,
    buildFeasibility: 9,
    speedToRevenue: 8,
    rawSignals: [
      { source: "r/HVAC", quote: "Double-booked three jobs today because my tech texted the wrong customer. I need real scheduling software that doesn't cost $500/month.", engagement: 156, date: "2026-02-26" },
      { source: "r/HVAC", quote: "ServiceTitan wants $3K/month for 5 techs. I'm a small shop, not a franchise. Where's the middle ground?", engagement: 203, date: "2026-02-27" },
    ],
    competitiveLandscape: [
      { name: "ServiceTitan", description: "Enterprise field service management", gap: "Too expensive for small shops ($3K+/mo)" },
      { name: "Housecall Pro", description: "Mid-market field service", gap: "Still $100+/tech/mo, cluttered features" },
    ],
    aiAnalysis: "A solid War Room Winner, Commander. The pain is real and the market is large, but whitespace is narrower than it appears — Jobber and Housecall Pro exist, even if they're overpriced for small shops. Your angle would be radical simplicity at a fraction of the cost.",
    ventureId: null,
    discoveredAt: "2026-02-26T10:00:00Z",
    scoredAt: "2026-02-26T10:05:00Z",
    opportunityName: "FieldSync",
    elevatorPitch: "Dead-simple scheduling for small HVAC shops at 1/10th the cost of ServiceTitan.",
    subIndustry: null,
    painSummary: "Small HVAC contractors with 3-10 techs have no affordable scheduling solution, relying on texts and spreadsheets.",
    proposedSolution: "Lightweight mobile-first scheduling app with auto-dispatch and customer notifications.",
    saturationLevel: "cracked_door",
    saturationDetail: "Competitors exist but are overpriced for small shops.",
    existingCompetitors: [
      { name: "ServiceTitan", weakness: "Too expensive ($3K+/mo)" },
      { name: "Housecall Pro", weakness: "$100+/tech/mo, cluttered" },
    ],
    blueOceanAngle: "Radical simplicity at $29/tech/mo for shops under 10 techs.",
    signalCount: 2,
    sourcePlatforms: ["reddit"],
    confidence: "medium",
    clusterIds: [],
    legacy: false,
  },
  {
    id: "opp-003",
    title: "MedSpa AI booking & consultation prep",
    description: "MedSpa owners struggling with no-shows, consultation prep time, and intake form chaos. AI could automate pre-visit questionnaires and reduce consult time by 50%.",
    sourceType: "manual",
    sourceUrl: "",
    sourceCommunity: "MedSpa owner network",
    industry: "MedSpa / Aesthetics",
    status: "building",
    compositeScore: 88,
    painIntensity: 7,
    purchasingPower: 9,
    targetability: 9,
    marketGrowth: 8,
    whitespace: 7,
    monopolyPotential: 6,
    domainProximity: 10,
    distributionAdvantage: 9,
    buildFeasibility: 8,
    speedToRevenue: 9,
    rawSignals: [
      { source: "MedSpa Forum", quote: "We lose $2,000/week in no-shows. I need something that handles confirmations AND reschedules automatically.", engagement: 45, date: "2026-02-20" },
    ],
    competitiveLandscape: [
      { name: "Boulevard", description: "Luxury salon/spa scheduling", gap: "No AI consultation prep or intake automation" },
      { name: "Zenoti", description: "Enterprise spa management", gap: "Expensive, complex, overkill for single-location MedSpas" },
    ],
    aiAnalysis: "Commander, this is your home turf. MedSpa is your inner ring — domain proximity at maximum. Your existing agency relationships give you distribution that no SaaS startup can match. The combination of AI consultation prep and smart booking is a genuine 2-for-1 value stack.",
    ventureId: "ven-001",
    discoveredAt: "2026-02-20T14:00:00Z",
    scoredAt: "2026-02-20T14:05:00Z",
    opportunityName: "MedSpa Booking AI", elevatorPitch: "AI-powered booking and consultation prep that reduces MedSpa no-shows by 40%.", subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: "cracked_door" as const, saturationDetail: null, existingCompetitors: [{ name: "Boulevard", weakness: "No AI consultation prep" }], blueOceanAngle: null, signalCount: 1, sourcePlatforms: ["manual"], confidence: "high" as const, clusterIds: [], legacy: false,
  },
  {
    id: "opp-004",
    title: "Restaurant menu QR code analytics gap",
    description: "Restaurant owners using QR menus post-COVID but zero analytics on what customers view, how long they browse, or what they skip.",
    sourceType: "trend_signal",
    sourceUrl: "https://reddit.com/r/restaurantowners/comments/ghi789",
    sourceCommunity: "r/restaurantowners",
    industry: "Food & Beverage",
    status: "discovered",
    compositeScore: 76,
    painIntensity: 6,
    purchasingPower: 6,
    targetability: 8,
    marketGrowth: 5,
    whitespace: 7,
    monopolyPotential: 4,
    domainProximity: 7,
    distributionAdvantage: 7,
    buildFeasibility: 9,
    speedToRevenue: 7,
    rawSignals: [
      { source: "r/restaurantowners", quote: "We switched to QR menus and have zero idea which items people actually look at. Our POS only shows what they order, not what they considered.", engagement: 89, date: "2026-02-28" },
    ],
    competitiveLandscape: [
      { name: "Toast", description: "Restaurant POS with basic menu management", gap: "No QR menu analytics" },
    ],
    aiAnalysis: "Watch List, Commander. The pain is real but not severe — restaurants have bigger problems. The purchasing power is moderate. However, your F&B domain proximity and the simplicity of the build make this worth monitoring.",
    ventureId: null,
    discoveredAt: "2026-02-28T09:00:00Z",
    scoredAt: "2026-02-28T09:05:00Z",
    opportunityName: null, elevatorPitch: null, subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: null, saturationDetail: null, existingCompetitors: null, blueOceanAngle: null, signalCount: 1, sourcePlatforms: ["reddit"], confidence: "medium" as const, clusterIds: [], legacy: false,
  },
  {
    id: "opp-005",
    title: "Optometry patient recall system",
    description: "Optometrists manually tracking when patients need annual exams. Losing revenue from patients who simply forget to come back.",
    sourceType: "pain_scanner",
    sourceUrl: "https://reddit.com/r/optometry/comments/jkl012",
    sourceCommunity: "r/optometry",
    industry: "Optometry / Eye Care",
    status: "discovered",
    compositeScore: 71,
    painIntensity: 6,
    purchasingPower: 7,
    targetability: 7,
    marketGrowth: 5,
    whitespace: 6,
    monopolyPotential: 4,
    domainProximity: 3,
    distributionAdvantage: 5,
    buildFeasibility: 8,
    speedToRevenue: 7,
    rawSignals: [
      { source: "r/optometry", quote: "We estimate 40% of patients don't come back for their annual exam. That's $200K+ in lost revenue for our practice.", engagement: 67, date: "2026-02-24" },
    ],
    competitiveLandscape: [
      { name: "Weave", description: "Patient communication platform", gap: "Generic recall, not optimized for optometry workflows" },
    ],
    aiAnalysis: "Watch List territory, Commander. The pain is measurable in dollars, which is promising. But optometry is outside your current ring and distribution channels. Worth monitoring if you expand into healthcare niches.",
    ventureId: null,
    discoveredAt: "2026-02-24T11:00:00Z",
    scoredAt: "2026-02-24T11:05:00Z",
    opportunityName: null, elevatorPitch: null, subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: null, saturationDetail: null, existingCompetitors: null, blueOceanAngle: null, signalCount: 1, sourcePlatforms: ["reddit"], confidence: "low" as const, clusterIds: [], legacy: false,
  },
  {
    id: "opp-006",
    title: "Freelance contractor invoice dispute resolution",
    description: "Freelancers on r/freelance constantly dealing with clients who dispute invoices, pay late, or ghost. No tool helps document and resolve disputes.",
    sourceType: "pain_scanner",
    sourceUrl: "https://reddit.com/r/freelance/comments/mno345",
    sourceCommunity: "r/freelance",
    industry: "Freelance / Gig Economy",
    status: "rejected",
    compositeScore: 58,
    painIntensity: 7,
    purchasingPower: 3,
    targetability: 6,
    marketGrowth: 5,
    whitespace: 5,
    monopolyPotential: 2,
    domainProximity: 4,
    distributionAdvantage: 5,
    buildFeasibility: 7,
    speedToRevenue: 4,
    rawSignals: [
      { source: "r/freelance", quote: "Client ghosted me after I delivered the project. $5,000 invoice unpaid for 3 months. I have no leverage.", engagement: 445, date: "2026-02-22" },
    ],
    competitiveLandscape: [
      { name: "FreshBooks", description: "Freelance invoicing", gap: "No dispute resolution features" },
    ],
    aiAnalysis: "Noise, Commander. High pain but catastrophically low purchasing power. Freelancers who can't collect invoices certainly can't pay for SaaS. The target audience is sympathetic but not profitable.",
    ventureId: null,
    discoveredAt: "2026-02-22T16:00:00Z",
    scoredAt: "2026-02-22T16:05:00Z",
    opportunityName: null, elevatorPitch: null, subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: "red_ocean" as const, saturationDetail: null, existingCompetitors: null, blueOceanAngle: null, signalCount: 1, sourcePlatforms: ["reddit"], confidence: "low" as const, clusterIds: [], legacy: false,
  },
  {
    id: "opp-007",
    title: "Auto detailing shop CRM & upsell engine",
    description: "Auto detailers tracking customers in spreadsheets, missing upsell timing for ceramic coating reapplication and seasonal packages.",
    sourceType: "pain_scanner",
    sourceUrl: "https://reddit.com/r/autodetailing/comments/pqr678",
    sourceCommunity: "r/autodetailing",
    industry: "Auto Detailing",
    status: "discovered",
    compositeScore: 67,
    painIntensity: 5,
    purchasingPower: 6,
    targetability: 7,
    marketGrowth: 6,
    whitespace: 6,
    monopolyPotential: 3,
    domainProximity: 3,
    distributionAdvantage: 6,
    buildFeasibility: 8,
    speedToRevenue: 6,
    rawSignals: [
      { source: "r/autodetailing", quote: "I know I should be contacting customers when their ceramic coating is due for maintenance but I literally track everything in a Google Sheet.", engagement: 78, date: "2026-02-27" },
    ],
    competitiveLandscape: [
      { name: "Urable", description: "Detailing-specific booking", gap: "No CRM or automated upsell timing" },
    ],
    aiAnalysis: "Parking Lot, Commander. Niche is real but small. The pain is mild — spreadsheets work, they just aren't optimal. Low urgency = low willingness to pay. Monitor but don't invest time.",
    ventureId: null,
    discoveredAt: "2026-02-27T13:00:00Z",
    scoredAt: "2026-02-27T13:05:00Z",
    opportunityName: null, elevatorPitch: null, subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: null, saturationDetail: null, existingCompetitors: null, blueOceanAngle: null, signalCount: 1, sourcePlatforms: ["reddit"], confidence: "low" as const, clusterIds: [], legacy: false,
  },
  {
    id: "opp-008",
    title: "Property management maintenance request AI triage",
    description: "Property managers overwhelmed by maintenance requests, can't prioritize emergency vs routine, tenants frustrated by slow response.",
    sourceType: "pain_scanner",
    sourceUrl: "https://reddit.com/r/propertymanagement/comments/stu901",
    sourceCommunity: "r/propertymanagement",
    industry: "Property Management",
    status: "discovered",
    compositeScore: 81,
    painIntensity: 8,
    purchasingPower: 8,
    targetability: 8,
    marketGrowth: 6,
    whitespace: 7,
    monopolyPotential: 5,
    domainProximity: 4,
    distributionAdvantage: 6,
    buildFeasibility: 7,
    speedToRevenue: 7,
    rawSignals: [
      { source: "r/propertymanagement", quote: "Got 47 maintenance requests this week. Half are 'urgent' but most are just clogged drains. I need AI to sort this mess.", engagement: 198, date: "2026-02-26" },
      { source: "r/landlord", quote: "Tenant submitted a request at 2am about a leaky faucet marked as emergency. Meanwhile the actual gas leak report got buried.", engagement: 156, date: "2026-02-27" },
    ],
    competitiveLandscape: [
      { name: "Buildium", description: "Property management platform", gap: "Basic ticketing, no AI triage or prioritization" },
      { name: "AppFolio", description: "Property management software", gap: "Maintenance portal exists but no intelligent routing" },
    ],
    aiAnalysis: "War Room Winner, Commander. Property managers have budget and pain. The AI triage angle is genuinely novel — existing tools treat all requests equally. Your content marketing can reach this audience through SEO for 'property management maintenance software'. Build feasibility is moderate — needs NLP classification, but Claude can handle that.",
    ventureId: null,
    discoveredAt: "2026-02-26T15:00:00Z",
    scoredAt: "2026-02-26T15:05:00Z",
    opportunityName: "PropTriage AI", elevatorPitch: "AI-powered maintenance request triage that separates emergencies from clogged drains.", subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: "cracked_door" as const, saturationDetail: null, existingCompetitors: [{ name: "Buildium", weakness: "No AI triage" }], blueOceanAngle: null, signalCount: 2, sourcePlatforms: ["reddit"], confidence: "medium" as const, clusterIds: [], legacy: false,
  },
  {
    id: "opp-009",
    title: "Tattoo studio consent & aftercare automation",
    description: "Tattoo studios still using paper consent forms, no digital aftercare instructions, losing repeat business due to zero follow-up.",
    sourceType: "builder_intel",
    sourceUrl: "https://reddit.com/r/tattoo/comments/vwx234",
    sourceCommunity: "r/tattoo",
    industry: "Tattoo / Body Art",
    status: "discovered",
    compositeScore: 63,
    painIntensity: 5,
    purchasingPower: 5,
    targetability: 6,
    marketGrowth: 5,
    whitespace: 7,
    monopolyPotential: 3,
    domainProximity: 2,
    distributionAdvantage: 4,
    buildFeasibility: 9,
    speedToRevenue: 5,
    rawSignals: [
      { source: "r/tattoo", quote: "We still do paper consent forms. Every artist has a different system. It's chaos when the health inspector shows up.", engagement: 45, date: "2026-02-28" },
    ],
    competitiveLandscape: [
      { name: "TattooPro", description: "Basic studio management", gap: "Consent forms are just PDF uploads, no smart aftercare" },
    ],
    aiAnalysis: "Parking Lot. Interesting niche but low purchasing power and far from your domain. The build is easy but distribution would be expensive to acquire.",
    ventureId: null,
    discoveredAt: "2026-02-28T14:00:00Z",
    scoredAt: "2026-02-28T14:05:00Z",
    opportunityName: null, elevatorPitch: null, subIndustry: null, painSummary: null, proposedSolution: null, saturationLevel: null, saturationDetail: null, existingCompetitors: null, blueOceanAngle: null, signalCount: 1, sourcePlatforms: ["reddit"], confidence: "low" as const, clusterIds: [], legacy: false,
  },
];

export const mockRevenueStreams: RevenueStream[] = [
  { id: "str-001", name: "MedSpa Packages", targetMonthly: 30000, targetClients: 15, avgDealSizeLow: 1000, avgDealSizeHigh: 5000, isActive: true, leverageLevel: "medium", sortOrder: 0 },
  { id: "str-002", name: "SEO Services", targetMonthly: 8000, targetClients: 10, avgDealSizeLow: 750, avgDealSizeHigh: 900, isActive: true, leverageLevel: "medium", sortOrder: 1 },
  { id: "str-003", name: "Web Design + Hosting", targetMonthly: 5000, targetClients: 8, avgDealSizeLow: 500, avgDealSizeHigh: 800, isActive: true, leverageLevel: "high", sortOrder: 2 },
  { id: "str-004", name: "SaaS Products", targetMonthly: 10000, targetClients: 0, avgDealSizeLow: 0, avgDealSizeHigh: 0, isActive: true, leverageLevel: "high", sortOrder: 3 },
  { id: "str-005", name: "Automation-as-a-Service", targetMonthly: 20000, targetClients: 5, avgDealSizeLow: 3000, avgDealSizeHigh: 10000, isActive: true, leverageLevel: "high", sortOrder: 4 },
  { id: "str-006", name: "Content Machine", targetMonthly: 8000, targetClients: 10, avgDealSizeLow: 500, avgDealSizeHigh: 1000, isActive: true, leverageLevel: "medium", sortOrder: 5 },
];

export const mockRevenueEntries: RevenueEntry[] = [
  // Current month
  { id: "rev-001", streamId: "str-001", streamName: "MedSpa Packages", amount: 3000, clientName: "Glow Aesthetics", description: "Monthly retainer", date: "2026-03-01", isRecurring: true },
  { id: "rev-002", streamId: "str-001", streamName: "MedSpa Packages", amount: 1500, clientName: "Radiance MedSpa", description: "Website + social setup", date: "2026-03-01", isRecurring: false },
  { id: "rev-003", streamId: "str-002", streamName: "SEO Services", amount: 850, clientName: "Peak Dental", description: "Monthly SEO", date: "2026-03-01", isRecurring: true },
  { id: "rev-004", streamId: "str-002", streamName: "SEO Services", amount: 750, clientName: "Harbor Law", description: "Monthly SEO", date: "2026-03-01", isRecurring: true },
  { id: "rev-005", streamId: "str-006", streamName: "Content Machine", amount: 653.28, clientName: "FitLife Gym", description: "Social content package", date: "2026-03-01", isRecurring: true },
  { id: "rev-006", streamId: "str-005", streamName: "Automation-as-a-Service", amount: 500, clientName: "ClearView Optics", description: "Intake form automation", date: "2026-03-01", isRecurring: false },
  // Previous months for trend
  { id: "rev-007", streamId: "str-001", streamName: "MedSpa Packages", amount: 2000, clientName: "Glow Aesthetics", description: "Monthly retainer", date: "2026-02-01", isRecurring: true },
  { id: "rev-008", streamId: "str-002", streamName: "SEO Services", amount: 850, clientName: "Peak Dental", description: "Monthly SEO", date: "2026-02-01", isRecurring: true },
  { id: "rev-009", streamId: "str-002", streamName: "SEO Services", amount: 750, clientName: "Harbor Law", description: "Monthly SEO", date: "2026-02-01", isRecurring: true },
  { id: "rev-010", streamId: "str-006", streamName: "Content Machine", amount: 500, clientName: "FitLife Gym", description: "Social content", date: "2026-02-01", isRecurring: true },
  { id: "rev-011", streamId: "str-001", streamName: "MedSpa Packages", amount: 1500, clientName: "Glow Aesthetics", description: "Monthly retainer", date: "2026-01-01", isRecurring: true },
  { id: "rev-012", streamId: "str-002", streamName: "SEO Services", amount: 850, clientName: "Peak Dental", description: "Monthly SEO", date: "2026-01-01", isRecurring: true },
  { id: "rev-013", streamId: "str-002", streamName: "SEO Services", amount: 750, clientName: "Harbor Law", description: "Monthly SEO", date: "2026-01-01", isRecurring: true },
  { id: "rev-014", streamId: "str-005", streamName: "Automation-as-a-Service", amount: 3000, clientName: "Metro Plumbing", description: "CRM automation build", date: "2026-01-15", isRecurring: false },
  { id: "rev-015", streamId: "str-006", streamName: "Content Machine", amount: 500, clientName: "FitLife Gym", description: "Social content", date: "2026-01-01", isRecurring: true },
];

export const mockVentures: Venture[] = [
  {
    id: "ven-001",
    opportunityId: "opp-003",
    name: "MedSpa Booking AI",
    description: "AI-powered booking and consultation prep tool for MedSpa clinics. Reduces no-shows by 40% and cuts consultation prep time in half.",
    status: "building",
    githubUrl: "https://github.com/digitalempire/medspa-booking-ai",
    liveUrl: "",
    monthlyRevenue: 0,
    launchDate: null,
  },
  {
    id: "ven-002",
    opportunityId: null,
    name: "Digital Empire Agency Site",
    description: "The agency website — lead generation machine for all service packages.",
    status: "launched",
    githubUrl: "https://github.com/digitalempire/agency-site",
    liveUrl: "https://digitalempiremarketing.com",
    monthlyRevenue: 0,
    launchDate: "2025-11-01",
  },
  {
    id: "ven-003",
    opportunityId: null,
    name: "Content Machine Platform",
    description: "Internal content generation and scheduling platform. Powering the Content Machine revenue stream.",
    status: "launched",
    githubUrl: "https://github.com/digitalempire/content-machine",
    liveUrl: "https://content.digitalempire.com",
    monthlyRevenue: 653.28,
    launchDate: "2026-01-15",
  },
];

export const mockMilestones: Milestone[] = [
  { id: "ms-001", title: "First 10 Clients", description: "Reach 10 total paying clients across all streams", metricType: "clients", targetValue: 10, currentValue: 5, targetDate: "2026-04-01", status: "in_progress" },
  { id: "ms-002", title: "$20K Monthly Revenue", description: "Hit $20,000 in monthly recurring revenue", metricType: "revenue", targetValue: 20000, currentValue: 7253.28, targetDate: "2026-06-01", status: "in_progress" },
  { id: "ms-003", title: "Launch First SaaS", description: "Ship and launch the first SaaS product from the Big Board pipeline", metricType: "ventures_launched", targetValue: 1, currentValue: 0, targetDate: "2026-05-01", status: "at_risk" },
  { id: "ms-004", title: "50 Opportunities Evaluated", description: "Score and evaluate 50 unique opportunities through Strangelove", metricType: "opportunities_evaluated", targetValue: 50, currentValue: 9, targetDate: "2026-06-01", status: "in_progress" },
  { id: "ms-005", title: "$83,333 Monthly Target", description: "The Doomsday Machine reaches full activation — $83,333/month", metricType: "revenue", targetValue: 83333, currentValue: 7253.28, targetDate: "2027-03-01", status: "not_started" },
];

export const mockAdvantageProfile: AdvantageProfile = {
  industriesKnown: [
    { name: "MedSpa / Aesthetics", proximityRating: 10 },
    { name: "Food & Beverage", proximityRating: 7 },
    { name: "Dental", proximityRating: 6 },
    { name: "Legal Services", proximityRating: 5 },
    { name: "Home Services", proximityRating: 4 },
    { name: "Fitness / Wellness", proximityRating: 5 },
  ],
  skills: ["Marketing Strategy", "SEO", "Content Marketing", "Web Design", "Vibe Coding", "Sales", "Paid Ads", "Social Media", "AI Automation", "Copywriting"],
  distributionChannels: [
    { name: "Agency Client Base", strength: 9 },
    { name: "Content Marketing / SEO", strength: 8 },
    { name: "Paid Ads (Meta, Google)", strength: 7 },
    { name: "Social Media (LinkedIn, IG)", strength: 6 },
    { name: "Email List", strength: 4 },
    { name: "Partnerships / Referrals", strength: 5 },
  ],
  productsShipped: [
    { name: "Digital Empire Agency Site", category: "Website", date: "2025-11-01" },
    { name: "Content Machine Platform", category: "Internal Tool", date: "2026-01-15" },
    { name: "MedSpa Lead Gen Funnels", category: "Marketing Funnel", date: "2025-12-01" },
  ],
  budgetMonthlyMax: 500,
};

export const mockScanLogs: ScanLog[] = [
  {
    id: "scan-001",
    agentType: "pain_scanner",
    sourcesScanned: ["r/veterinary", "r/HVAC", "r/propertymanagement", "r/autodetailing", "r/restaurantowners", "r/optometry", "r/freelance", "r/tattoo"],
    opportunitiesFound: 23,
    opportunitiesScored: 9,
    startedAt: "2026-02-28T02:00:00Z",
    completedAt: "2026-02-28T02:04:32Z",
    status: "completed",
  },
  {
    id: "scan-002",
    agentType: "builder_intel",
    sourcesScanned: ["Hacker News Top", "Product Hunt Daily"],
    opportunitiesFound: 5,
    opportunitiesScored: 1,
    startedAt: "2026-02-28T02:05:00Z",
    completedAt: "2026-02-28T02:06:15Z",
    status: "completed",
  },
];

export const mockAchievements: Achievement[] = [
  { id: "ach-001", badgeKey: "first_strike", title: "First Strike", description: "Score your first 80+ opportunity", icon: "Crosshair", xpReward: 50, earnedAt: "2026-02-20T14:05:00Z" },
  { id: "ach-002", badgeKey: "precious_bodily_fluids", title: "Precious Bodily Fluids", description: "Maintain a 10-day login streak", icon: "Shield", xpReward: 100, earnedAt: "2026-02-28T08:00:00Z" },
  { id: "ach-003", badgeKey: "big_board_initiate", title: "Big Board Initiate", description: "Evaluate your first 5 opportunities", icon: "LayoutDashboard", xpReward: 25, earnedAt: "2026-02-25T10:00:00Z" },
  { id: "ach-004", badgeKey: "total_commitment", title: "Total Commitment", description: "Launch your first venture", icon: "Rocket", xpReward: 200, earnedAt: null },
  { id: "ach-005", badgeKey: "i_can_walk", title: "I Can Walk!", description: "Earn first revenue from a new venture", icon: "DollarSign", xpReward: 250, earnedAt: null },
  { id: "ach-006", badgeKey: "doomsday_activated", title: "Doomsday Activated", description: "Reach $83,333 monthly revenue", icon: "Gauge", xpReward: 1000, earnedAt: null },
  { id: "ach-007", badgeKey: "war_room_veteran", title: "War Room Veteran", description: "Evaluate 50 opportunities", icon: "Star", xpReward: 150, earnedAt: null },
];

export const mockDeepDiveMessages: DeepDiveMessage[] = [
  { id: "msg-001", opportunityId: "opp-001", role: "strangelove", content: "Good morning, Commander. I have completed my analysis of the veterinary insurance claim automation opportunity. The signals are... remarkable. Shall we review the intelligence?", createdAt: "2026-02-25T08:10:00Z" },
  { id: "msg-002", opportunityId: "opp-001", role: "commander", content: "Yes, walk me through the key findings. What makes this a Strike Now?", createdAt: "2026-02-25T08:11:00Z" },
  { id: "msg-003", opportunityId: "opp-001", role: "strangelove", content: "Three factors converge with extraordinary precision, Commander.\n\nFirst, the pain is *operational*, not aspirational. These clinics are losing measurable revenue — one reported $12,000 in unpaid claims from missed deadlines alone. When the pain has a dollar figure, the willingness to pay is self-evident.\n\nSecond, the whitespace is genuine. PetDesk handles scheduling. Shepherd handles practice management. But NO ONE has built a multi-carrier insurance claim automation tool for veterinary clinics. This is not a crowded market wearing a blue ocean disguise — it is genuinely unoccupied territory.\n\nThird, your distribution advantage is potent. Content marketing to veterinary professionals is straightforward — they gather in identifiable communities, they search for solutions actively, and your SEO capabilities can capture that intent.", createdAt: "2026-02-25T08:12:00Z" },
  { id: "msg-004", opportunityId: "opp-001", role: "commander", content: "Run the Hormozi Value Equation for this opportunity.", createdAt: "2026-02-25T08:15:00Z" },
  { id: "msg-005", opportunityId: "opp-001", role: "strangelove", content: "With pleasure, Commander. The Hormozi Value Equation states:\n\n**Value = (Dream Outcome × Perceived Likelihood) / (Time Delay × Effort & Sacrifice)**\n\nLet us apply it:\n\n**Dream Outcome**: Eliminate 90% of insurance claim paperwork. Recover $50K+/year in unpaid claims. Free up 2-3 hours daily for actual patient care. *Rating: 9/10*\n\n**Perceived Likelihood**: If the tool integrates with major pet insurance carriers (Trupanion, Nationwide, ASPCA), the outcome is nearly guaranteed. Auto-submission + status tracking = claims don't get lost. *Rating: 8/10*\n\n**Time Delay**: A clinic could see results within the first week of onboarding — submit a claim, track it, get paid faster. Very short time to value. *Rating: 2/10 (low is good)*\n\n**Effort & Sacrifice**: Minimal switching cost — they're currently doing this manually or with paper. Moving to software is strictly additive. *Rating: 2/10 (low is good)*\n\n**Value Score: (9 × 8) / (2 × 2) = 72/4 = 18x** — Extraordinary value ratio, Commander. This is a Grand Slam Offer waiting to be packaged.", createdAt: "2026-02-25T08:16:00Z" },
  { id: "msg-006", opportunityId: "opp-001", role: "commander", content: "What about Thiel's monopoly question? Can we build a defensible position?", createdAt: "2026-02-25T08:20:00Z" },
  { id: "msg-007", opportunityId: "opp-001", role: "strangelove", content: "An astute inquiry, Commander. Let me apply Thiel's framework:\n\n**Can you start with a small market and monopolize it?** Yes — veterinary clinics are a defined, countable market (~32,000 practices in the US). You don't need the whole market. 500 clinics at $200/month = $100K/month.\n\n**Network effects?** Moderate — each carrier integration makes the platform more valuable for all clinics. If you become the standard, carriers will integrate with YOU.\n\n**Switching costs?** High once adopted — claim history, carrier connections, workflow integration. A clinic won't switch tools mid-year with pending claims in the system.\n\n**The verdict:** Defensibility is achievable. First-mover in a specific niche with carrier integrations creates a genuine moat. This is not winner-take-all, but it IS winner-take-most within veterinary insurance claims.\n\nI must note, Commander — my hand is trembling with excitement. This is precisely the type of opportunity The Big Board was designed to surface.", createdAt: "2026-02-25T08:21:00Z" },
];

export const mockBriefing: Briefing = {
  greeting: "Good morning, Commander. The Big Board has been updated.",
  revenue: {
    currentMonth: 7253.28,
    target: 83333,
    percentage: 8.7,
    gap: 76079.72,
  },
  overnightScan: {
    newOpportunities: 3,
    topScore: 84,
    topOpportunityTitle: "HVAC contractor scheduling chaos",
  },
  topActions: [
    "Review 92-score STRIKE NOW opportunity: Veterinary insurance claim automation",
    "Follow up with Radiance MedSpa prospect — pipeline: $3,000/mo potential",
    "Content Machine: 2 client posts due today (FitLife Gym)",
  ],
  strangeloveQuote: "The whole point of The Big Board is lost if you don't check it every morning, Commander.",
};

// ── Helper functions ──

export function getCurrentMonthRevenue(): number {
  const now = new Date();
  return mockRevenueEntries
    .filter((e) => {
      const d = new Date(e.date);
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    })
    .reduce((sum, e) => sum + e.amount, 0);
}

export function getRevenueByStream(): { streamId: string; streamName: string; total: number }[] {
  const now = new Date();
  const currentEntries = mockRevenueEntries.filter((e) => {
    const d = new Date(e.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });

  return mockRevenueStreams.map((stream) => ({
    streamId: stream.id,
    streamName: stream.name,
    total: currentEntries.filter((e) => e.streamId === stream.id).reduce((sum, e) => sum + e.amount, 0),
  }));
}

export function getMonthlyTrend(): { month: string; revenue: number; target: number }[] {
  const months = ["2026-01", "2026-02", "2026-03"];
  const labels = ["Jan", "Feb", "Mar"];

  return months.map((m, i) => ({
    month: labels[i]!,
    revenue: mockRevenueEntries
      .filter((e) => e.date.startsWith(m))
      .reduce((sum, e) => sum + e.amount, 0),
    target: 83333,
  }));
}
